import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {UIChart} from 'primeng/chart';
import {MultiSelectModule} from 'primeng/multiselect';
import {
  InvitationOfferOverviewResponse,
  ProviderTenderInvitationOfferOverviewResponse,
  RfqControllerService,
} from '@frontend/shared-api';
import {firstValueFrom} from 'rxjs';
import {AppCurrencyPipe, AppNumberPipe} from '@frontend/shared';
import {ProviderOverallResult} from '../../types/overall-results.types';
import {TranslocoService} from '@jsverse/transloco';
import {CostComparisonService} from './services/cost-comparison.service';
import {DirectusTooltipComponent} from '../../../../../../../../shared/components/directus-tooltip/directus-tooltip.component';
import {
  ALL_COST_TYPES,
  CombinedDetailedBreakdownRow,
  CostChartLegendItem,
  CostDevelopmentChartView,
  CostType,
  ProviderCostComparisonVm,
} from './cost-comparison.types';
import {LoadingComponent} from '../../../../../../../../shared/components/loading/loading.component';

@Component({
  selector: 'app-cost-comparison',
  imports: [
    AppNumberPipe,
    CommonModule,
    FormsModule,
    TranslocoPipe,
    UIChart,
    MultiSelectModule,
    DirectusTooltipComponent,
    AppCurrencyPipe,
    LoadingComponent,
  ],
  templateUrl: './cost-comparison.component.html',
  styleUrl: './cost-comparison.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostComparisonComponent {
  private readonly rfqController = inject(RfqControllerService);
  private readonly costComparisonService = inject(CostComparisonService);
  private readonly translocoService = inject(TranslocoService);

  readonly projectId = input<number | undefined>(undefined);
  readonly providerResults = input<ProviderOverallResult[]>([]);

  protected readonly isLoading = signal(false);
  protected readonly costDevelopmentChartView = signal<CostDevelopmentChartView>('years');
  protected readonly selectedCostTypes = signal<CostType[]>([...ALL_COST_TYPES]);
  protected readonly selectedHrToolIds = signal<string[]>([]);
  private readonly hasInitializedHrToolFilter = signal(false);
  private readonly invitationOverviewsByToolId = signal<
    Map<string, InvitationOfferOverviewResponse>
  >(new Map());
  protected readonly costTypeFilterOptions: Array<{
    labelKey: string;
    value: CostType;
  }> = [
    {labelKey: 'project.steps.rfq.serviceFees', value: 'serviceFees'},
    {labelKey: 'project.steps.rfq.implementationFees', value: 'implementationCosts'},
    {labelKey: 'project.steps.rfq.integrationCosts', value: 'integrationCosts'},
    {labelKey: 'project.steps.rfq.otherCosts', value: 'otherCosts'},
  ];

  protected readonly providerCostCards = computed<ProviderCostComparisonVm[]>(() => {
    const overviewsByToolId = this.invitationOverviewsByToolId();
    return this.costComparisonService.buildProviderCostCards(
      this.providerResults(),
      overviewsByToolId,
    );
  });

  protected readonly chartOptions = {
    maintainAspectRatio: false,
    animation: false,
    layout: {
      padding: {
        top: 6,
        bottom: 16,
      },
    },
    datasets: {
      bar: {
        categoryPercentage: 0.88,
        barPercentage: 1,
        maxBarThickness: 60,
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: 'rgba(229, 231, 235, 0.8)',
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.2)',
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          color: 'rgba(229, 231, 235, 0.8)',
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.2)',
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (tooltipItems: Array<{label?: string}>): string => {
            const label = tooltipItems[0]?.label ?? '';
            const yearMatch = /^Y(\d+)$/.exec(label);
            return yearMatch ? `Year ${yearMatch[1]}` : label;
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };

  protected readonly totalChartOptions = {
    maintainAspectRatio: false,
    animation: false,
    indexAxis: 'y' as const,
    layout: {
      padding: {
        top: 6,
        bottom: 16,
      },
    },
    datasets: {
      bar: {
        categoryPercentage: 0.88,
        barPercentage: 1,
        maxBarThickness: 60,
      },
    },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          color: 'rgba(229, 231, 235, 0.8)',
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.2)',
        },
      },
      y: {
        stacked: true,
        ticks: {
          color: 'rgba(229, 231, 235, 0.8)',
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.2)',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  protected readonly chartLegendItems = computed<CostChartLegendItem[]>(() => {
    const paletteByToolId = this.providerPaletteByToolId();
    return this.filteredProviderCostCards().map((provider) => ({
      providerName: provider.translatedName,
      color:
        paletteByToolId.get(provider.hrToolDirectusId)?.serviceFees ?? 'rgba(148, 163, 184, 0.7)',
    }));
  });

  protected readonly providerPaletteByToolId = computed<
    Map<
      string,
      {
        serviceFees: string;
        implementationCosts: string;
        integrationCosts: string;
        otherCosts: string;
      }
    >
  >(() => {
    const paletteMap = new Map<
      string,
      {
        serviceFees: string;
        implementationCosts: string;
        integrationCosts: string;
        otherCosts: string;
      }
    >();
    this.providerCostCards().forEach((provider, index) => {
      paletteMap.set(
        provider.hrToolDirectusId,
        this.costComparisonService.getProviderPalette(index),
      );
    });
    return paletteMap;
  });

  protected readonly filteredProviderCostCards = computed<ProviderCostComparisonVm[]>(() => {
    const selectedHrToolIds = this.selectedHrToolIds();
    if (selectedHrToolIds.length === 0) {
      return [];
    }
    if (selectedHrToolIds.length === this.providerCostCards().length) {
      return this.providerCostCards();
    }
    const selectedIdSet = new Set(selectedHrToolIds);
    return this.providerCostCards().filter((provider) =>
      selectedIdSet.has(provider.hrToolDirectusId),
    );
  });

  protected readonly hrToolFilterOptions = computed<Array<{label: string; value: string}>>(() => {
    return this.providerCostCards().map((provider) => ({
      label: provider.translatedName,
      value: provider.hrToolDirectusId,
    }));
  });

  protected readonly detailedBreakdownYears = computed<number[]>(() => {
    const maxYears = this.providerCostCards().reduce(
      (max, provider) => Math.max(max, provider.years.length),
      0,
    );
    return Array.from({length: maxYears}, (_, index) => index + 1);
  });

  protected readonly groupedStackedChartData = computed(() => {
    const providers = this.filteredProviderCostCards();
    const paletteByToolId = this.providerPaletteByToolId();
    const years = this.detailedBreakdownYears();
    const labels = years.map((year) => `Y${year}`);
    const selectedCostTypeSet = new Set(this.selectedCostTypes());

    const serviceLabel = this.translocoService.translate('project.steps.rfq.serviceFees');
    const implementationLabel = this.translocoService.translate(
      'project.steps.rfq.implementationFees',
    );
    const integrationLabel = this.translocoService.translate('project.steps.rfq.integrationCosts');
    const otherLabel = this.translocoService.translate('project.steps.rfq.otherCosts');

    const datasets = providers.flatMap((provider) => {
      const palette = paletteByToolId.get(provider.hrToolDirectusId);
      if (!palette) {
        return [];
      }
      const stack = provider.hrToolDirectusId;
      const allDatasets = [
        {
          id: 'serviceFees' as const,
          label: `${provider.translatedName} · ${serviceLabel}`,
          stack,
          data: years.map((year) => provider.yearlyBreakdown.serviceFees[year - 1] ?? 0),
          backgroundColor: palette.serviceFees,
          borderWidth: 0,
        },
        {
          id: 'implementationCosts' as const,
          label: `${provider.translatedName} · ${implementationLabel}`,
          stack,
          data: years.map((year) => provider.yearlyBreakdown.implementationCosts[year - 1] ?? 0),
          backgroundColor: palette.implementationCosts,
          borderWidth: 0,
        },
        {
          id: 'integrationCosts' as const,
          label: `${provider.translatedName} · ${integrationLabel}`,
          stack,
          data: years.map((year) => provider.yearlyBreakdown.integrationCosts[year - 1] ?? 0),
          backgroundColor: palette.integrationCosts,
          borderWidth: 0,
        },
        {
          id: 'otherCosts' as const,
          label: `${provider.translatedName} · ${otherLabel}`,
          stack,
          data: years.map((year) => provider.yearlyBreakdown.otherCosts[year - 1] ?? 0),
          backgroundColor: palette.otherCosts,
          borderWidth: 0,
        },
      ];
      return allDatasets.filter((dataset) => selectedCostTypeSet.has(dataset.id));
    });

    return {labels, datasets};
  });

  protected readonly totalStackedChartData = computed(() => {
    const providers = this.filteredProviderCostCards();
    const paletteByToolId = this.providerPaletteByToolId();
    const labels = providers.map((provider) => provider.translatedName);
    const selectedCostTypeSet = new Set(this.selectedCostTypes());
    const serviceLabel = this.translocoService.translate('project.steps.rfq.serviceFees');
    const implementationLabel = this.translocoService.translate(
      'project.steps.rfq.implementationFees',
    );
    const integrationLabel = this.translocoService.translate('project.steps.rfq.integrationCosts');
    const otherLabel = this.translocoService.translate('project.steps.rfq.otherCosts');

    return {
      labels,
      datasets: [
        {
          id: 'serviceFees' as const,
          label: serviceLabel,
          data: providers.map((provider) =>
            this.costComparisonService.sum(provider.yearlyBreakdown.serviceFees),
          ),
          backgroundColor: providers.map(
            (provider) =>
              paletteByToolId.get(provider.hrToolDirectusId)?.serviceFees ??
              'rgba(148, 163, 184, 0.7)',
          ),
          borderWidth: 0,
        },
        {
          id: 'implementationCosts' as const,
          label: implementationLabel,
          data: providers.map((provider) =>
            this.costComparisonService.sum(provider.yearlyBreakdown.implementationCosts),
          ),
          backgroundColor: providers.map(
            (provider) =>
              paletteByToolId.get(provider.hrToolDirectusId)?.implementationCosts ??
              'rgba(148, 163, 184, 0.7)',
          ),
          borderWidth: 0,
        },
        {
          id: 'integrationCosts' as const,
          label: integrationLabel,
          data: providers.map((provider) =>
            this.costComparisonService.sum(provider.yearlyBreakdown.integrationCosts),
          ),
          backgroundColor: providers.map(
            (provider) =>
              paletteByToolId.get(provider.hrToolDirectusId)?.integrationCosts ??
              'rgba(148, 163, 184, 0.7)',
          ),
          borderWidth: 0,
        },
        {
          id: 'otherCosts' as const,
          label: otherLabel,
          data: providers.map((provider) =>
            this.costComparisonService.sum(provider.yearlyBreakdown.otherCosts),
          ),
          backgroundColor: providers.map(
            (provider) =>
              paletteByToolId.get(provider.hrToolDirectusId)?.otherCosts ??
              'rgba(148, 163, 184, 0.7)',
          ),
          borderWidth: 0,
        },
      ].filter((dataset) => selectedCostTypeSet.has(dataset.id)),
    };
  });

  protected readonly combinedDetailedBreakdownRows = computed<CombinedDetailedBreakdownRow[]>(
    () => {
      const years = this.detailedBreakdownYears();
      const paletteByToolId = this.providerPaletteByToolId();
      return this.providerCostCards().flatMap((provider) =>
        provider.detailedRows.map((row, rowIndex) => ({
          id: `${provider.hrToolDirectusId}-${row.labelKey}`,
          providerName: provider.translatedName,
          providerColor:
            paletteByToolId.get(provider.hrToolDirectusId)?.serviceFees ??
            'rgba(148, 163, 184, 0.7)',
          currencyLocale: provider.currencyLocale,
          currencyCode: provider.currencyCode,
          isFirstProviderRow: rowIndex === 0,
          labelKey: row.labelKey,
          values: years.map((year) => row.values[year - 1] ?? 0),
          total: row.total,
          isTotalRow: row.labelKey.endsWith('.total'),
        })),
      );
    },
  );

  constructor() {
    effect(() => {
      const currentProjectId = this.projectId();
      if (currentProjectId === undefined) {
        this.invitationOverviewsByToolId.set(new Map());
        return;
      }
      void this.loadInvitationOfferOverviews(currentProjectId);
    });

    effect(() => {
      const providerOptions = this.hrToolFilterOptions();
      if (providerOptions.length === 0) {
        this.selectedHrToolIds.set([]);
        this.hasInitializedHrToolFilter.set(false);
        return;
      }

      const selectedIds = this.selectedHrToolIds();
      const optionIdSet = new Set(providerOptions.map((option) => option.value));
      const stillValidSelectedIds = selectedIds.filter((id) => optionIdSet.has(id));

      if (!this.hasInitializedHrToolFilter()) {
        this.selectedHrToolIds.set(providerOptions.map((option) => option.value));
        this.hasInitializedHrToolFilter.set(true);
        return;
      }

      if (stillValidSelectedIds.length !== selectedIds.length) {
        this.selectedHrToolIds.set(stillValidSelectedIds);
      }
    });
  }

  private async loadInvitationOfferOverviews(projectId: number): Promise<void> {
    this.isLoading.set(true);
    try {
      const overviews = await firstValueFrom(
        this.rfqController.getInvitationOfferOverviews({projectId}),
      ).catch((): ProviderTenderInvitationOfferOverviewResponse[] => []);

      const nextMap = new Map<string, InvitationOfferOverviewResponse>();
      overviews.forEach((overviewItem) => {
        nextMap.set(
          overviewItem.providerTenantMappingResponse.hrToolDirectusId,
          overviewItem.invitationOfferOverviewResponse,
        );
      });

      this.invitationOverviewsByToolId.set(nextMap);
    } catch (error) {
      console.error('Error loading cost comparison data:', error);
      this.invitationOverviewsByToolId.set(new Map());
    } finally {
      this.isLoading.set(false);
    }
  }
}
